import React from "react";
import ReactDOM from "react-dom";
import { DateTime } from "luxon";
import { useModal } from "@root/client/modal";
import { EventIcon } from "@common/eventIcon";
import { EventPopup } from "@scenes/eventPopup";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { useScreenWatcher } from "@common/screenWatcher";
import classNames from "classnames";
import { Props } from "./types";
import styles from "./styles.scss";

function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

function intToRGB(i: number) {
  const c = (i & 0x00ffffff).toString(16).toUpperCase();
  return "00000".substring(0, 6 - c.length) + c;
}

function generateRandomColor(name: string) {
  return `#${intToRGB(hashCode(name))}`;
}

function isColorLight(color: string) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
}

function Event({ event }: Props) {
  const { pushView } = useModal();
  const userTimezone = DateTime.local().zoneName;
  const backgroundColor = generateRandomColor(event.project.name);
  const isLight = isColorLight(backgroundColor);
  const startDt = DateTime.fromISO(event.startTime, { zone: "utc" }).setZone(
    userTimezone,
  );
  const endDt = DateTime.fromISO(event.endTime, { zone: "utc" }).setZone(
    userTimezone,
  );
  const startLabel = startDt.isValid
    ? startDt.toLocaleString({ hour: "2-digit", minute: "2-digit" })
    : "—";
  const endLabel = endDt.isValid
    ? endDt.toLocaleString({ hour: "2-digit", minute: "2-digit" })
    : "—";
  const isShort =
    startDt.isValid &&
    endDt.isValid &&
    endDt.toMillis() - startDt.toMillis() <= 30 * 60 * 1000;
  const [popupPosition, setPopupPosition] = React.useState<{
    x: number;
    y: number;
    side: "right" | "left";
  } | null>(null);
  const eventRef = React.useRef<HTMLDivElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const screen = useScreenWatcher();

  const handleEventClick = React.useCallback(
    (e: React.MouseEvent, show: () => void) => {
      e.stopPropagation();
      if (eventRef.current) {
        const rect = eventRef.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.right;
        const side = spaceRight >= 280 + 20 ? "right" : "left";
        setPopupPosition({ x: e.clientX, y: e.clientY, side });
        show();
      }
    },
    [],
  );

  const popupStyle = React.useMemo(() => {
    if (!popupPosition) return {};
    const estimatedPopupHeight = 320;
    const margin = 10;
    const halfHeight = estimatedPopupHeight / 2;
    const clampedY = Math.min(
      Math.max(popupPosition.y, margin + halfHeight),
      screen.height - margin - halfHeight,
    );
    return {
      position: "fixed" as const,
      left:
        popupPosition.side === "right" ? `${popupPosition.x + 15}px` : "auto",
      right:
        popupPosition.side === "left"
          ? `${screen.width - popupPosition.x + 15}px`
          : "auto",
      top: `${clampedY}px`,
      transform: "translateY(-50%)",
    };
  }, [popupPosition, screen]);

  return (
    <VisibilitySwitch
      renderChildren={(isVisible, show, hide) => {
        React.useEffect(() => {
          const handleClickOutside = (event: MouseEvent) => {
            if (
              isVisible &&
              popupRef.current &&
              eventRef.current &&
              !popupRef.current.contains(event.target as Node) &&
              !eventRef.current.contains(event.target as Node)
            ) {
              hide();
            }
          };

          if (isVisible) {
            setTimeout(() => {
              document.addEventListener("mousedown", handleClickOutside);
            }, 100);
          }

          return () => {
            document.removeEventListener("mousedown", handleClickOutside);
          };
        }, [isVisible]);

        return (
          <div className={styles.eventWrapper}>
            <div
              ref={eventRef}
              className={classNames(styles.event, {
                [styles.active]: isVisible,
              })}
              onClick={(e) => handleEventClick(e, show)}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.background}
                style={{
                  backgroundColor,
                  opacity: 0.6,
                }}
              />

              <div
                className={styles.leftBorder}
                style={{
                  backgroundColor: isLight
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(255,255,255,0.3)",
                }}
              />

              <div
                className={classNames(styles.main, {
                  [styles.short]: isShort,
                })}
              >
                <p
                  className={styles.time}
                  style={{ color: isLight ? "#000000" : "#FFFFFF" }}
                >
                  {startLabel} - {endLabel}
                </p>
                <p
                  className={styles.name}
                  style={{
                    color: isLight ? "#000000" : "#FFFFFF",
                  }}
                >
                  <EventIcon
                    className={classNames(styles.icon, styles.eventIcon)}
                  />
                  {event.name}
                </p>
              </div>
            </div>

            {isVisible &&
              popupPosition &&
              ReactDOM.createPortal(
                <div
                  ref={popupRef}
                  className={styles.eventPopupMain}
                  style={popupStyle}
                >
                  <EventPopup project={event.project} event={event} />
                </div>,
                document.body,
              )}
          </div>
        );
      }}
    />
  );
}

export { Event };
